import { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, Sparkles, ArrowRight, Play, Plus, Trash2, Award,
  Bot, ChevronRight, TrendingUp, AlertCircle, X, Info, Mail, Send,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const httpAuth = () => ({ Authorization: `Bearer ${localStorage.getItem("wt_token")}` });

const MODEL_LOGO = {
  chatgpt: { label: "ChatGPT",     tone: "bg-emerald-50 text-emerald-700 ring-emerald-200" },
  gemini:  { label: "Gemini",      tone: "bg-sky-50 text-sky-700 ring-sky-200" },
  claude:  { label: "Claude",      tone: "bg-orange-50 text-orange-700 ring-orange-200" },
};

const INDUSTRIES = [
  ["doctors","Doctors"],["dentists","Dentists"],["auto-dealers","Auto Dealers"],
  ["driver-schools","Driving Schools"],["plumbers","Plumbers"],["electricians","Electricians"],
  ["catering","Catering"],["bakeries","Bakeries"],["farms","Farms"],
  ["travel","Travel & Tourism"],["professional-services","Professional Services"],
  ["other","Other"],
];

export default function AIVisibilityMonitor() {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [prompts, setPrompts] = useState([]);
  const [runs, setRuns]       = useState([]);
  const [openRun, setOpenRun] = useState(null);
  const [running, setRunning] = useState(false);
  const [digestOn, setDigestOn]     = useState(false);
  const [digestBusy, setDigestBusy] = useState(false);

  // Setup form
  const [brand, setBrand]         = useState("");
  const [industry, setIndustry]   = useState("other");
  const [location, setLocation]   = useState("");
  const [service, setService]     = useState("");
  const [competitors, setCompetitors] = useState("");
  const [newPrompt, setNewPrompt] = useState("");

  const reload = useCallback(async () => {
    try {
      const [{ data: p }, { data: r }, { data: s }] = await Promise.all([
        axios.get(`${API}/ai-visibility/prompts`, { headers: httpAuth() }),
        axios.get(`${API}/ai-visibility/runs`, { headers: httpAuth() }),
        axios.get(`${API}/ai-visibility/digest/settings`, { headers: httpAuth() }),
      ]);
      setPrompts(p.prompts || []);
      setRuns(r.runs || []);
      setDigestOn(!!s.enabled);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load AI visibility.");
    }
  }, []);

  useEffect(() => {
    document.title = "AI Visibility Monitor | WebsiteTech.pro";
    if (user === undefined) return;
    if (!user) { setLoading(false); return; }
    (async () => { await reload(); setLoading(false); })();
  }, [user, reload]);

  const seedDefaults = async () => {
    if (!location.trim() || !service.trim()) {
      toast.error("Add your location and service first");
      return;
    }
    try {
      const { data } = await axios.post(`${API}/ai-visibility/prompts/default`,
        { brand: brand.trim(), industry, location: location.trim(),
          service: service.trim() },
        { headers: httpAuth() });
      toast.success(data.created
        ? `Added ${data.created} starter prompts`
        : "Starter prompts already added");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const addPrompt = async () => {
    if (!newPrompt.trim()) return;
    try {
      await axios.post(`${API}/ai-visibility/prompts`,
        { prompt: newPrompt.trim(), industry },
        { headers: httpAuth() });
      setNewPrompt("");
      toast.success("Prompt added");
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const deletePrompt = async (pid) => {
    try {
      await axios.delete(`${API}/ai-visibility/prompts/${pid}`,
        { headers: httpAuth() });
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const runNow = async () => {
    if (prompts.length === 0) {
      toast.error("Add at least one prompt first");
      return;
    }
    setRunning(true);
    try {
      const { data } = await axios.post(`${API}/ai-visibility/run`, {
        brand: brand.trim(),
        competitors: competitors.split(",").map(c => c.trim()).filter(Boolean),
      }, { headers: httpAuth(), timeout: 180000 });
      toast.success(`Simulation done — score ${data.run.visibility_score}/100`);
      setOpenRun(data.run);
      reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Simulation failed");
    } finally { setRunning(false); }
  };

  const openRunDetail = async (rid) => {
    try {
      const { data } = await axios.get(`${API}/ai-visibility/runs/${rid}`,
        { headers: httpAuth() });
      setOpenRun(data);
    } catch (e) { toast.error("Could not open run"); }
  };

  const toggleDigest = async (next) => {
    setDigestBusy(true);
    try {
      await axios.post(`${API}/ai-visibility/digest/settings`,
        { enabled: next }, { headers: httpAuth() });
      setDigestOn(next);
      toast.success(next ? "Subscribed — Monday morning recap is on"
                          : "Weekly digest turned off");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setDigestBusy(false); }
  };

  const sendDigestNow = async () => {
    setDigestBusy(true);
    try {
      const { data } = await axios.post(`${API}/ai-visibility/digest/send-now`,
        {}, { headers: httpAuth() });
      toast.success(data.sent ? "Recap emailed"
                                : "Recap prepared (email not configured)");
    } catch (e) { toast.error(e?.response?.data?.detail || "No recent run"); }
    finally { setDigestBusy(false); }
  };

  if (user === undefined || loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-slate-50">
        <Loader2 className="w-6 h-6 animate-spin text-slate-500" />
      </div>
    );
  }
  if (!user) {
    return (
      <div className="min-h-screen bg-slate-50" data-testid="aivis-signin">
        <Navbar />
        <main className="max-w-3xl mx-auto px-4 py-16 text-center">
          <Bot className="w-10 h-10 text-slate-400 mx-auto" />
          <h1 className="text-3xl font-bold text-slate-900 mt-3">AI Visibility Monitor</h1>
          <p className="text-slate-600 mt-2">
            See when the top AI assistants — ChatGPT, Gemini, and Claude — recommend your business (and when they name a competitor instead).
          </p>
          <Link to="/login?redirect=/ai-visibility-monitor"
                data-testid="aivis-signin-btn"
                className="mt-6 inline-flex items-center gap-2 rounded-lg bg-slate-900 hover:bg-slate-800 text-white px-5 py-2.5 text-sm font-bold">
            Sign in <ArrowRight className="w-4 h-4" />
          </Link>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="aivis-page">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 py-8 grid gap-6">
        <header>
          <div className="text-[11px] uppercase tracking-widest text-orange-600 font-bold">
            WebsiteTech · Growth System
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mt-1">
            AI Visibility Monitor
          </h1>
          <p className="text-slate-600 mt-1 max-w-3xl">
            We ask the three biggest AI assistants — <b>ChatGPT</b>, <b>Gemini</b>, and <b>Claude</b> — the questions your buyers ask. You see if they mention you, name a competitor, or answer generically.
          </p>
          <div className="mt-3 inline-flex items-center gap-1 text-xs text-slate-500 bg-slate-100 rounded-full px-3 py-1">
            <Info className="w-3.5 h-3.5" /> AI Search Simulation · no live search-engine data — this shows how the top LLMs answer today.
          </div>
        </header>

        {/* ---- Setup ---- */}
        <section className="rounded-xl bg-white ring-1 ring-slate-200 p-5"
                 data-testid="aivis-setup">
          <h2 className="font-bold text-slate-900">1 · Tell us who you are</h2>
          <div className="grid md:grid-cols-2 gap-3 mt-3">
            <Field label="Your brand name">
              <input value={brand} onChange={(e) => setBrand(e.target.value)}
                      placeholder="e.g. Sunrise Dental"
                      data-testid="aivis-brand"
                      className="rounded-lg ring-1 ring-slate-200 px-3 py-2 text-sm w-full" />
            </Field>
            <Field label="Industry">
              <select value={industry} onChange={(e) => setIndustry(e.target.value)}
                       data-testid="aivis-industry"
                       className="rounded-lg ring-1 ring-slate-200 px-3 py-2 text-sm w-full">
                {INDUSTRIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </Field>
            <Field label="Location you serve">
              <input value={location} onChange={(e) => setLocation(e.target.value)}
                      placeholder="e.g. Austin, TX"
                      data-testid="aivis-location"
                      className="rounded-lg ring-1 ring-slate-200 px-3 py-2 text-sm w-full" />
            </Field>
            <Field label="Main service">
              <input value={service} onChange={(e) => setService(e.target.value)}
                      placeholder="e.g. teeth cleaning"
                      data-testid="aivis-service"
                      className="rounded-lg ring-1 ring-slate-200 px-3 py-2 text-sm w-full" />
            </Field>
            <Field label="Competitors (comma separated — optional)" full>
              <input value={competitors} onChange={(e) => setCompetitors(e.target.value)}
                      placeholder="e.g. Downtown Dental, Bright Smile Co"
                      data-testid="aivis-competitors"
                      className="rounded-lg ring-1 ring-slate-200 px-3 py-2 text-sm w-full" />
            </Field>
          </div>
          <div className="mt-4 flex gap-2 flex-wrap">
            <button onClick={seedDefaults}
                    className="rounded-lg bg-slate-900 hover:bg-slate-800 text-white px-4 py-2 text-sm font-bold inline-flex items-center gap-1"
                    data-testid="aivis-seed">
              <Sparkles className="w-4 h-4" /> Load starter prompts for my industry
            </button>
          </div>
        </section>

        {/* ---- Prompts ---- */}
        <section className="rounded-xl bg-white ring-1 ring-slate-200 p-5"
                 data-testid="aivis-prompts">
          <div className="flex flex-wrap items-baseline gap-3 justify-between">
            <h2 className="font-bold text-slate-900">
              2 · Prompts to test <span className="text-xs text-slate-500 ml-1 font-normal">({prompts.length})</span>
            </h2>
            <button onClick={runNow} disabled={running || prompts.length === 0}
                    className="rounded-lg bg-orange-500 hover:bg-orange-600 text-white px-5 py-2 text-sm font-bold inline-flex items-center gap-2 disabled:opacity-50"
                    data-testid="aivis-run">
              {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}
              {running ? "Simulating…" : "Run simulation"}
            </button>
          </div>
          <div className="mt-3 flex gap-2">
            <input value={newPrompt} onChange={(e) => setNewPrompt(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addPrompt()}
                    placeholder="Type a question a buyer would ask an AI…"
                    className="flex-1 rounded-lg ring-1 ring-slate-200 px-3 py-2 text-sm"
                    data-testid="aivis-new-prompt" />
            <button onClick={addPrompt}
                    className="rounded-lg ring-1 ring-slate-200 bg-white hover:bg-slate-50 px-3 py-2 text-sm font-bold inline-flex items-center gap-1"
                    data-testid="aivis-add-prompt">
              <Plus className="w-4 h-4" /> Add
            </button>
          </div>
          <ul className="mt-3 space-y-2">
            {prompts.length === 0 && (
              <li className="text-sm text-slate-500 text-center py-6"
                  data-testid="aivis-prompts-empty">
                No prompts yet. Load starter prompts above, or type your own.
              </li>
            )}
            {prompts.map(p => (
              <li key={p.id}
                  className="rounded-lg ring-1 ring-slate-200 bg-slate-50 px-3 py-2 flex items-center gap-2 text-sm"
                  data-testid={`aivis-prompt-${p.id}`}>
                <span className="flex-1 text-slate-800">{p.prompt}</span>
                {p.seed === "default" && (
                  <span className="text-[10px] uppercase tracking-widest font-bold text-slate-400">default</span>
                )}
                <button onClick={() => deletePrompt(p.id)}
                        className="text-slate-400 hover:text-rose-600"
                        data-testid={`aivis-delete-${p.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </li>
            ))}
          </ul>
        </section>

        {/* ---- Weekly Digest ---- */}
        <section className="rounded-xl bg-gradient-to-br from-slate-900 to-slate-950 text-white p-5 flex flex-wrap items-center gap-4 justify-between"
                 data-testid="aivis-digest">
          <div className="flex items-start gap-3">
            <Mail className="w-5 h-5 text-fuchsia-400 mt-0.5 shrink-0" />
            <div>
              <div className="text-[10px] uppercase tracking-widest text-fuchsia-300 font-bold">
                Monday morning recap
              </div>
              <div className="text-lg font-bold mt-0.5">
                Weekly AI Visibility digest
              </div>
              <div className="text-slate-300 text-sm mt-1 max-w-xl">
                Get a Monday-morning email showing week-over-week movement across ChatGPT, Gemini, and Claude — plus new competitors named and prompts you missed.
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={sendDigestNow} disabled={digestBusy}
                    data-testid="aivis-digest-send-now"
                    className="rounded-lg ring-1 ring-white/20 bg-white/5 hover:bg-white/10 text-white px-3 py-2 text-xs font-bold inline-flex items-center gap-1 disabled:opacity-50">
              <Send className="w-3.5 h-3.5" /> Send me a preview now
            </button>
            <Link to="/ai-visibility-monitor/digest-preview"
                    data-testid="aivis-digest-full-preview"
                    className="rounded-lg ring-1 ring-white/20 bg-white/5 hover:bg-white/10 text-white px-3 py-2 text-xs font-bold inline-flex items-center gap-1">
              Full-page preview →
            </Link>
            {user?.role === "admin" && (
              <Link to="/admin/digest-analytics"
                      data-testid="aivis-digest-analytics-link"
                      className="rounded-lg ring-1 ring-white/20 bg-white/5 hover:bg-white/10 text-white px-3 py-2 text-xs font-bold inline-flex items-center gap-1">
                Analytics →
              </Link>
            )}
            <button onClick={() => toggleDigest(!digestOn)} disabled={digestBusy}
                    data-testid="aivis-digest-toggle"
                    className={`rounded-lg px-4 py-2 text-sm font-bold inline-flex items-center gap-1 transition ${
                      digestOn ? "bg-emerald-500 hover:bg-emerald-600 text-white"
                                : "bg-orange-500 hover:bg-orange-600 text-white"}`}>
              {digestBusy ? <Loader2 className="w-4 h-4 animate-spin" />
                            : digestOn ? "Subscribed · turn off"
                                        : "Subscribe · every Monday"}
            </button>
          </div>
        </section>

        {/* ---- Runs history ---- */}
        <section className="rounded-xl bg-white ring-1 ring-slate-200 p-5"
                 data-testid="aivis-runs">
          <h2 className="font-bold text-slate-900">3 · Past simulations</h2>
          {runs.length === 0 && (
            <div className="text-sm text-slate-500 text-center py-6"
                 data-testid="aivis-runs-empty">
              No simulations yet. Load prompts and click Run.
            </div>
          )}
          {runs.length > 0 && (
            <ul className="mt-3 divide-y divide-slate-100">
              {runs.map(r => (
                <li key={r.id}
                    className="py-3 flex items-center gap-3 cursor-pointer hover:bg-slate-50 rounded-lg px-2"
                    onClick={() => openRunDetail(r.id)}
                    data-testid={`aivis-run-${r.id}`}>
                  <ScoreBadge score={r.visibility_score} />
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-slate-900 truncate">
                      {r.brand || "Unnamed brand"}
                    </div>
                    <div className="text-xs text-slate-500">
                      {r.brand_hits}/{r.total_pairs} mentions ·
                      {" "}{new Date(r.created_at).toLocaleString()}
                    </div>
                  </div>
                  <ChevronRight className="w-4 h-4 text-slate-400" />
                </li>
              ))}
            </ul>
          )}
        </section>
      </main>

      {openRun && <RunDrawer run={openRun} onClose={() => setOpenRun(null)} />}
    </div>
  );
}

/* --------------------------- Sub-components ------------------------------ */
function Field({ label, children, full }) {
  return (
    <div className={full ? "md:col-span-2" : ""}>
      <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-1">{label}</div>
      {children}
    </div>
  );
}

function ScoreBadge({ score }) {
  const tone = score >= 70 ? "bg-emerald-100 text-emerald-800"
                : score >= 30 ? "bg-amber-100 text-amber-800"
                : "bg-rose-100 text-rose-800";
  return (
    <div className={`rounded-lg ${tone} px-3 py-2 min-w-[56px] text-center font-extrabold text-lg`}
         data-testid="aivis-score-badge">
      {score}
    </div>
  );
}

function RunDrawer({ run, onClose }) {
  const [expanded, setExpanded] = useState(new Set());
  const toggle = (id) => setExpanded(prev => {
    const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n;
  });
  const modelHitPct = useMemo(() => {
    const out = {};
    Object.entries(run.by_model || {}).forEach(([code, s]) => {
      out[code] = s.total ? Math.round((s.hits / s.total) * 100) : 0;
    });
    return out;
  }, [run]);
  return (
    <div className="fixed inset-0 z-50 flex" data-testid="aivis-run-drawer">
      <div className="flex-1 bg-slate-900/50" onClick={onClose} />
      <aside className="w-full max-w-3xl bg-white h-full overflow-y-auto shadow-xl">
        <div className="p-5 border-b border-slate-200 flex items-start justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest font-bold text-orange-600">
              AI visibility · {new Date(run.created_at).toLocaleString()}
            </div>
            <h2 className="text-xl font-bold text-slate-900 mt-1">
              {run.brand || "Unnamed brand"}
            </h2>
            <div className="mt-2 flex items-baseline gap-3">
              <ScoreBadge score={run.visibility_score} />
              <div className="text-sm text-slate-600">
                <b>{run.brand_hits}</b> of {run.total_pairs} model-prompt pairs mentioned you.
              </div>
            </div>
          </div>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900"
                  data-testid="aivis-run-close"><X className="w-5 h-5" /></button>
        </div>

        {/* Per-model bar */}
        <div className="p-5">
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">
            Mentions by model
          </div>
          <div className="grid grid-cols-3 gap-3" data-testid="aivis-by-model">
            {Object.entries(run.by_model || {}).map(([code, s]) => (
              <div key={code}
                   className="rounded-lg ring-1 ring-slate-200 p-3">
                <div className="flex items-center gap-2 text-sm">
                  <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-full ring-1 ring-inset ${(MODEL_LOGO[code] || MODEL_LOGO.claude).tone}`}>
                    {(MODEL_LOGO[code] || {}).label || code}
                  </span>
                </div>
                <div className="text-2xl font-extrabold text-slate-900 mt-1">
                  {s.hits}<span className="text-sm text-slate-400 font-bold">/{s.total}</span>
                </div>
                <div className="text-xs text-slate-500 mt-0.5">
                  {modelHitPct[code]}% hit rate
                </div>
                <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-emerald-500"
                       style={{ width: `${modelHitPct[code]}%` }} />
                </div>
                {s.positive > 0 && (
                  <div className="mt-1 text-[10px] text-emerald-700">
                    · {s.positive} positive
                  </div>
                )}
                {s.negative > 0 && (
                  <div className="text-[10px] text-rose-700">
                    · {s.negative} negative
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Competitors */}
          {run.top_competitors && run.top_competitors.length > 0 && (
            <div className="mt-5" data-testid="aivis-competitors">
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">
                Competitors named instead of you
              </div>
              <div className="flex flex-wrap gap-2">
                {run.top_competitors.map(c => (
                  <span key={c.name}
                        className="rounded-full ring-1 ring-slate-200 bg-slate-50 px-3 py-1 text-sm text-slate-700 inline-flex items-center gap-1">
                    <Award className="w-3.5 h-3.5" /> {c.name}
                    <b className="ml-1 text-slate-500">×{c.count}</b>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Per-prompt drill-down */}
          <div className="mt-5">
            <div className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-2">
              Prompt-by-prompt results
            </div>
            <ul className="space-y-2" data-testid="aivis-prompt-results">
              {(run.results || []).map(pp => {
                const hits = pp.results.filter(r => r.mentioned).length;
                const total = pp.results.length;
                const isOpen = expanded.has(pp.prompt_id);
                return (
                  <li key={pp.prompt_id}
                      className="rounded-lg ring-1 ring-slate-200 bg-white overflow-hidden">
                    <button onClick={() => toggle(pp.prompt_id)}
                            className="w-full text-left p-3 hover:bg-slate-50 flex items-center gap-2"
                            data-testid={`aivis-prompt-result-${pp.prompt_id}`}>
                      <div className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-bold text-xs ring-1
                                        ${hits === total ? "bg-emerald-50 text-emerald-700 ring-emerald-200"
                                          : hits > 0 ? "bg-amber-50 text-amber-700 ring-amber-200"
                                          : "bg-rose-50 text-rose-700 ring-rose-200"}`}>
                        {hits}/{total}
                      </div>
                      <div className="flex-1 text-sm font-bold text-slate-800">{pp.prompt}</div>
                      <ChevronRight className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? "rotate-90" : ""}`} />
                    </button>
                    {isOpen && (
                      <div className="border-t border-slate-100 p-3 space-y-3 bg-slate-50">
                        {pp.results.map((r, i) => (
                          <div key={i} className="text-sm">
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`text-[10px] uppercase tracking-widest px-1.5 py-0.5 rounded-full ring-1 ring-inset ${(MODEL_LOGO[r.model] || MODEL_LOGO.claude).tone}`}>
                                {(MODEL_LOGO[r.model] || {}).label || r.model}
                              </span>
                              {r.mentioned ? (
                                <span className="text-[10px] uppercase font-bold text-emerald-700 inline-flex items-center gap-1">
                                  <TrendingUp className="w-3 h-3" /> mentioned · {r.sentiment}
                                </span>
                              ) : (
                                <span className="text-[10px] uppercase font-bold text-rose-700 inline-flex items-center gap-1">
                                  <AlertCircle className="w-3 h-3" /> not mentioned
                                </span>
                              )}
                              {r.competitor_mentions?.length > 0 && (
                                <span className="text-[10px] text-slate-500">
                                  · named: {r.competitor_mentions.join(", ")}
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-slate-700 whitespace-pre-line bg-white ring-1 ring-slate-200 rounded p-2">
                              {r.answer}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          </div>
        </div>
      </aside>
    </div>
  );
}
